import { useState, type FormEvent, type DragEvent } from 'react';
import {
  WEEKDAYS,
  WEEKDAY_LABELS,
  addWeeks,
  dateOfWeekday,
  weekKeyOf,
  weekRangeLabel,
  type Todo,
  type Weekday,
  type WeekKey,
} from '@td/shared';
import {
  useWeekTodos,
  useCreateTodo,
  useUpdateTodo,
  useDeleteTodo,
  useStopRepeating,
  useReorderTodos,
  useMoveTodo,
} from '../hooks/useTodos';
import { todayWeekday } from '../lib/dates';
import { HeaderMenu } from '../components/HeaderMenu';

function TodoRow({
  todo,
  week,
  onDragStart,
}: {
  todo: Todo;
  week: WeekKey;
  onDragStart: (e: DragEvent, todo: Todo) => void;
}) {
  const update = useUpdateTodo();
  const remove = useDeleteTodo();
  const stopRepeating = useStopRepeating();
  const move = useMoveTodo();
  const [editing, setEditing] = useState(false);
  const [text, setText] = useState(todo.text);

  function commit() {
    const trimmed = text.trim();
    setEditing(false);
    if (trimmed && trimmed !== todo.text) update.mutate({ id: todo.id, week, text: trimmed });
    else setText(todo.text);
  }

  const repeating = todo.kind === 'weekly';

  return (
    <li
      draggable={!editing}
      onDragStart={(e) => onDragStart(e, todo)}
      className={[
        'group relative flex items-start gap-2 rounded-lg px-2 py-1.5 text-sm transition-colors',
        todo.completed ? 'bg-sage/20 text-olive' : 'hover:bg-linen',
      ].join(' ')}
    >
      <input
        type="checkbox"
        checked={todo.completed}
        onChange={(e) => update.mutate({ id: todo.id, week, completed: e.target.checked })}
        aria-label={`Mark "${todo.text}" ${todo.completed ? 'not done' : 'done'}`}
        className="mt-0.5 h-4 w-4 shrink-0 cursor-pointer accent-olive"
      />

      {editing ? (
        <input
          className="field flex-1 py-0.5 text-sm"
          value={text}
          autoFocus
          onChange={(e) => setText(e.target.value)}
          onBlur={commit}
          onKeyDown={(e) => {
            if (e.key === 'Enter') commit();
            if (e.key === 'Escape') {
              setText(todo.text);
              setEditing(false);
            }
          }}
        />
      ) : (
        <button
          onClick={() => setEditing(true)}
          className={[
            'flex-1 cursor-text text-left leading-snug',
            todo.completed ? 'line-through' : '',
          ].join(' ')}
        >
          {todo.text}
          {repeating && (
            <span className="ml-1.5 text-[11px] text-clay" title="Repeats every week">
              ↻
            </span>
          )}
        </button>
      )}

      {/*
        Moving it somewhere else.

        Only on things that are not done. A finished item has nowhere to go,
        and an arrow beside it would be one more thing to read past.
      */}
      {!todo.completed && (
        <HeaderMenu
          label={`Move "${todo.text}" to another day`}
          width={210}
          className="shrink-0 rounded px-1 text-stone opacity-0 transition-opacity hover:text-terracotta group-hover:opacity-100 focus:opacity-100"
          glyph={<span aria-hidden>→</span>}
        >
          {(close) => (
            <div className="space-y-0.5">
              <p className="px-1 pb-1 text-[11px] uppercase tracking-wide text-stone">Move it to</p>

              {WEEKDAYS.filter((d) => d !== todo.weekday).map((day) => (
                <button
                  key={day}
                  className="block w-full rounded px-2 py-1.5 text-left text-sm text-walnut hover:bg-linen"
                  onClick={() => {
                    move.mutate({ id: todo.id, weekday: day, week, fromWeek: week });
                    close();
                  }}
                >
                  {WEEKDAY_LABELS[day]}
                </button>
              ))}

              {/*
                Friday afternoon's real answer. Kept apart from the days above,
                because it is a different kind of decision: not "later this
                week" but "this is next week's problem now".
              */}
              <div className="!mt-1.5 border-t border-sand pt-1.5">
                <button
                  className="block w-full rounded px-2 py-1.5 text-left text-sm text-espresso hover:bg-linen"
                  onClick={() => {
                    move.mutate({
                      id: todo.id,
                      weekday: 'mon',
                      week: addWeeks(week, 1),
                      fromWeek: week,
                    });
                    close();
                  }}
                >
                  Next Monday
                </button>
              </div>
            </div>
          )}
        </HeaderMenu>
      )}

      {repeating ? (
        /*
          The repeating options used to be a hand-rolled panel whose only way
          out was its own Cancel button. Through the shared menu they close on
          an outside click and on Escape, like everything else that pops out.
        */
        <HeaderMenu
          label={`What to do with "${todo.text}"`}
          width={220}
          className="shrink-0 rounded px-1 text-stone opacity-0 transition-opacity hover:text-rust group-hover:opacity-100 focus:opacity-100"
          glyph={<span aria-hidden>✕</span>}
        >
          {(close) => (
            <div className="space-y-0.5">
              <button
                className="block w-full rounded px-2 py-1.5 text-left text-sm text-walnut hover:bg-linen"
                onClick={() => {
                  remove.mutate({ id: todo.id, week, scope: 'week' });
                  close();
                }}
              >
                Skip just this week
              </button>
              <button
                className="block w-full rounded px-2 py-1.5 text-left text-sm text-walnut hover:bg-linen"
                onClick={() => {
                  stopRepeating.mutate({ id: todo.id, week });
                  close();
                }}
              >
                Stop repeating after this week
              </button>
              <button
                className="block w-full rounded px-2 py-1.5 text-left text-sm text-rust hover:bg-linen"
                onClick={() => {
                  remove.mutate({ id: todo.id, week, scope: 'series' });
                  close();
                }}
              >
                Delete every week
              </button>
            </div>
          )}
        </HeaderMenu>
      ) : (
        <button
          onClick={() => remove.mutate({ id: todo.id, week, scope: 'series' })}
          aria-label={`Remove "${todo.text}"`}
          className="shrink-0 text-stone opacity-0 transition-opacity hover:text-rust group-hover:opacity-100 focus:opacity-100"
        >
          ✕
        </button>
      )}
    </li>
  );
}

function DayColumn({
  weekday,
  week,
  todos,
  isCurrentWeek,
}: {
  weekday: Weekday;
  week: WeekKey;
  todos: Todo[];
  isCurrentWeek: boolean;
}) {
  const create = useCreateTodo();
  const reorder = useReorderTodos();
  const [text, setText] = useState('');
  const [repeat, setRepeat] = useState(false);
  const [dragOver, setDragOver] = useState(false);

  const isToday = isCurrentWeek && weekday === todayWeekday();
  const done = todos.filter((t) => t.completed).length;
  const dayNumber = dateOfWeekday(week, weekday).getDate();

  function onSubmit(e: FormEvent) {
    e.preventDefault();
    const trimmed = text.trim();
    if (!trimmed) return;
    create.mutate({
      text: trimmed,
      weekday,
      weekStart: week,
      kind: repeat ? 'weekly' : 'once',
    });
    setText('');
  }

  function onDrop(e: DragEvent) {
    e.preventDefault();
    setDragOver(false);
    const id = e.dataTransfer.getData('text/plain');
    if (!id) return;
    const ids = [...todos.filter((t) => t.id !== id).map((t) => t.id), id];
    reorder.mutate({ weekday, week, ids });
  }

  return (
    <section
      onDragOver={(e) => {
        e.preventDefault();
        setDragOver(true);
      }}
      onDragLeave={() => setDragOver(false)}
      onDrop={onDrop}
      className={[
        'flex flex-col rounded-card border bg-white p-3 shadow-soft transition-colors',
        dragOver ? 'border-clay bg-linen/60' : isToday ? 'border-terracotta/50' : 'border-sand/70',
      ].join(' ')}
    >
      <div className="mb-2 flex items-baseline justify-between">
        <h2 className={['text-sm', isToday ? 'text-terracotta' : 'text-espresso'].join(' ')}>
          {WEEKDAY_LABELS[weekday]} <span className="text-[11px] text-stone">{dayNumber}</span>
          {isToday && <span className="ml-1.5 text-[11px] text-terracotta">today</span>}
        </h2>
        {todos.length > 0 && (
          <span className="text-[11px] text-stone">
            {done}/{todos.length}
          </span>
        )}
      </div>

      <ul className="mb-2 flex-1 space-y-0.5">
        {todos.length === 0 && <li className="px-2 py-1 text-xs text-stone">Nothing yet</li>}
        {todos.map((t) => (
          <TodoRow
            key={t.id}
            todo={t}
            week={week}
            onDragStart={(e, todo) => e.dataTransfer.setData('text/plain', todo.id)}
          />
        ))}
      </ul>

      <form onSubmit={onSubmit}>
        <input
          className="field py-1.5 text-sm"
          placeholder="Add an item"
          value={text}
          onChange={(e) => setText(e.target.value)}
          aria-label={`Add a to-do for ${WEEKDAY_LABELS[weekday]}`}
        />
        {text.trim() && (
          <label className="mt-1.5 flex items-center gap-1.5 px-1 text-[11px] text-walnut">
            <input
              type="checkbox"
              checked={repeat}
              onChange={(e) => setRepeat(e.target.checked)}
              className="h-3 w-3 accent-terracotta"
            />
            Repeat every week
          </label>
        )}
      </form>
    </section>
  );
}

export function TodosPage() {
  const thisWeek = weekKeyOf();
  const [week, setWeek] = useState<WeekKey>(thisWeek);
  const { data, isLoading, error } = useWeekTodos(week);

  const todos = data?.todos ?? [];
  const isCurrentWeek = week === thisWeek;
  const offset = Math.round(
    (new Date(week).getTime() - new Date(thisWeek).getTime()) / (7 * 86_400_000),
  );

  const relative =
    offset === 0
      ? 'This week'
      : offset === 1
        ? 'Next week'
        : offset === -1
          ? 'Last week'
          : offset > 0
            ? `In ${offset} weeks`
            : `${Math.abs(offset)} weeks ago`;

  return (
    <div className="mx-auto max-w-7xl">
      <header className="page-header flex flex-wrap items-end justify-between gap-3">
        <h1 className="text-2xl">To-do list</h1>

        <div className="flex items-center gap-1">
          <button
            onClick={() => setWeek(addWeeks(week, -1))}
            className="btn-secondary px-2.5 py-1.5"
            aria-label="Previous week"
          >
            ←
          </button>
          <div className="min-w-[168px] px-2 text-center">
            <div className="text-sm text-espresso">{weekRangeLabel(week)}</div>
            <div className="text-[11px] text-stone">{relative}</div>
          </div>
          <button
            onClick={() => setWeek(addWeeks(week, 1))}
            className="btn-secondary px-2.5 py-1.5"
            aria-label="Next week"
          >
            →
          </button>
          {!isCurrentWeek && (
            <button onClick={() => setWeek(thisWeek)} className="btn-secondary ml-1.5">
              This week
            </button>
          )}
        </div>
      </header>

      {error ? (
        <p className="text-sm text-rust">Could not load that week.</p>
      ) : (
        <div
          className={[
            'grid gap-4 transition-opacity md:grid-cols-3 xl:grid-cols-5',
            isLoading ? 'opacity-50' : '',
          ].join(' ')}
        >
          {WEEKDAYS.map((day) => (
            <DayColumn
              key={day}
              weekday={day}
              week={week}
              isCurrentWeek={isCurrentWeek}
              todos={todos.filter((t) => t.weekday === day)}
            />
          ))}
        </div>
      )}
    </div>
  );
}
